import { useRef } from 'react';

import {
  PaymentMethodCreateParams,
  StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';
import { useElements, useStripe } from '@stripe/react-stripe-js';

import {
  ESdkEvents,
  EventParams,
  CardDetails,
  EPaymentProvider,
  EBEPaymentMethodsName,
  StripeFinalizePaymentResponse,
} from '../../../contracts';
import { useBlikForm } from './use-blik-form';
import { sendFEEvent } from '../../../eventUtil';
import { useOrderPolling } from './use-order-polling';
import { usePaymentState } from './use-payment-state';
import { useAPI } from '../../../services/api.service';
import { useCheckout } from '../../../hooks/use-checkout';
import { ECheckoutStatus } from '../../../constants/enums';
import { getCheckoutUrl } from '../../../utils/get-checkout-url';
import { AddressElementRef } from '../components/AddressElement/hooks/use-address-element-ref';
import { generateAppchargeErrorMessage } from '../../../utils/generate-appcharge-error-message';

interface useStripeSubmitParams {
  setOverlay: (value: boolean) => void;
  setApmTab?: (value: any) => void;
}

const sleep = async (ms: number) => new Promise((res) => setTimeout(res, ms));

// the router expect to get a different display name for link
const expressCheckoutEventToDisplayNameMapWithRouter: Record<string, string> = {
  link: 'Link',
  apple_pay: 'Apple',
  google_pay: 'Google Pay',
};

export function useStripeSubmit({ setOverlay, setApmTab }: useStripeSubmitParams) {
  const { API } = useAPI();
  const stripe = useStripe();
  const elements = useElements();
  const { showBoundary } = useErrorBoundary();
  const {
    checkoutData,
    getEventParams,
    selectedPaymentMethod,
    selectedPaymentMethodDisplayNameRef,
  } = useCheckout();
  const isStripePaymentInProgress = useRef(false);
  const { startOrderPolling } = useOrderPolling(handleError);
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setCheckoutStatus = usePaymentState((state) => state.setCheckoutStatus);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const fullAddress = usePaymentState((state) => state.fullAddress);
  const zipCode = usePaymentState((state) => state.zipCode);
  const shouldSaveCard = usePaymentState((state) => state.shouldSaveCard || false);
  const { triggerFormValidation, formValues, isFormValid } = useBlikForm();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });

  function handleError(errorMessage?: string) {
    const appChargeErrorMessage: string = generateAppchargeErrorMessage(t, errorMessage);
    setIsPaymentLoading(false);
    isStripePaymentInProgress.current = false;
    setErrorMessage(appChargeErrorMessage);
  }

  async function handleSubmit(
    isExpress: boolean | undefined,
    paymentMethodId: string | undefined,
    addressComponent?: AddressElementRef
  ) {
    if (!stripe || isStripePaymentInProgress.current) return;
    isStripePaymentInProgress.current = true;
    setErrorMessage('');
    setIsPaymentLoading(true);

    addressComponent?.validate();
    const isZipCodeError = isExpress ? false : addressComponent && !addressComponent.isValid();
    // workaround for blik with stripe
    if (selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.blikCheckout) {
      triggerFormValidation();
      if (!isFormValid || isZipCodeError) {
        handleError();
        return;
      }
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'p24',
        billing_details: {
          email: formValues.email,
        },
        p24: {
          bank: 'blik',
        },
      });

      if (error) {
        handleError();
        return;
      }

      finalizePayment({
        paymentProvider: EPaymentProvider.stripe,
        receipt_email: paymentMethod?.billing_details.email,
        params: {
          paymentMethodId: paymentMethod?.id,
        },
        paymentMethodDisplayName: selectedPaymentMethod.paymentMethodDisplayName,
        paymentMethodId: selectedPaymentMethod.id,
      });
      return;
    }

    const submitResult = await elements?.submit();
    if (submitResult?.error || isZipCodeError) {
      handleError();
      return;
    }

    const paymentMethodParams = getPaymentMethodParams(
      selectedPaymentMethod!.paymentMethodName,
      addressComponent?.values.zipCode || zipCode,
      addressComponent?.values.address || fullAddress
    );
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      elements: elements!,
      params: paymentMethodParams,
    });

    if (error) {
      handleError(error.message);
      return;
    }
    // specific logic for ideal full name validation bug ACDEV-14201
    if (selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.ideal) {
      const fullName = paymentMethod?.billing_details.name;
      if (fullName !== null && fullName.length < 3) {
        handleError(t('fullNameIdeal'));
        return;
      }
    }

    // move logic to external function
    let cardDetails: CardDetails | undefined;
    if (paymentMethod.card) {
      cardDetails = {
        bin: '',
        nameOnCard: '',
        lastFourDigits: paymentMethod.card?.last4 || '',
        expirationMonth: paymentMethod.card?.exp_month?.toString() || '',
        expirationYear: paymentMethod.card?.exp_year?.toString() || '',
        cardBrand: paymentMethod.card?.brand,
        fingerprint: paymentMethod.card?.fingerprint || '',
        countryOfIssuance: paymentMethod.card?.country || '',
      };
    }

    const isCardPaymentMethod =
      selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.card;

    const receiptEmail = paymentMethod.billing_details.email || checkoutData?.customer?.email || '';

    const finalizePaymentRequestBody = {
      paymentProvider: EPaymentProvider.stripe,
      receipt_email: receiptEmail,
      params: {
        paymentMethodId: paymentMethod.id,
        cardDetails,
        paymentMethodDetails: paymentMethod,
        shouldSaveCard: isCardPaymentMethod ? shouldSaveCard : undefined,
      },
      paymentMethodDisplayName: selectedPaymentMethodDisplayNameRef.current,
      paymentMethodId: paymentMethodId,
    };
    const FEeventParams: Partial<EventParams> = {
      isSavedPaymentMethodUsed: paymentMethod.type === 'link',
    };
    finalizePayment(finalizePaymentRequestBody, FEeventParams);
  }

  async function finalizePayment(body: any, FEeventParams?: Partial<EventParams>) {
    try {
      const data = await API.finalizePayment<StripeFinalizePaymentResponse>(body);
      handleServerResponse(data, FEeventParams);
    } catch (error: any) {
      const errResponse = error?.response?.data;
      setIsPaymentLoading(false);
      showBoundary(errResponse?.message);
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        ...FEeventParams,
        reason: errResponse.message,
        error: {
          isFatal: true,
          reason: errResponse.message,
        },
      });
    }
  }

  async function handleServerResponse(
    response: StripeFinalizePaymentResponse,
    FEeventParams?: Partial<EventParams>
  ) {
    if (response.stripe.status === 'succeeded') {
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
        ...getEventParams(),
        ...FEeventParams,
      });
      startOrderPolling(response.orderId, FEeventParams);
    } else if (response.stripe.status === 'failed') {
      handleError(response.appChargeErrorType);
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        ...FEeventParams,
        reason: response.stripe.message || '',
      });
    } else if (response.stripe.status === 'redirect') {
      // get the PaymentIntent object from stripe to check if there is a redirect URL
      const paymentIntentResult = await stripe?.retrievePaymentIntent(response.stripe.clientSecret);
      let url = paymentIntentResult?.paymentIntent?.next_action?.redirect_to_url?.url;

      // checking the payment intent to handle the case of cashapp on mobile
      const isCashApp = selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.cashapp;
      if (isMobile && isCashApp) {
        const checkoutUrl = getCheckoutUrl();
        url = `${checkoutUrl}/handle-payment/${response.stripe.clientSecret}/${checkoutData?.pks.stripe}`;
      }
      if (url) {
        handleRedirectAPM(url, response);
      } else {
        // if there is no redirect URL then we let stripe handle the next action (E.g. 3ds, cashapp on desktop, etc...)
        handlePaymentNextAction(response.stripe.clientSecret, response.orderId, FEeventParams);
      }
    }
  }

  async function handleRedirectAPM(url: string, response: any) {
    setOverlay(true);
    let retries = 2;
    const newtab = window.open(url as string, '_blank');
    if (newtab && setApmTab) setApmTab(newtab);
    let polling = true;
    while (polling) {
      await sleep(1000);
      const paymentIntentResults = await stripe?.retrievePaymentIntent(
        response.stripe.clientSecret
      );
      if (paymentIntentResults?.paymentIntent?.status === 'processing') {
        API.log({
          component: 'useSubmitPayment => handleRedirectAPM',
          params: { response, paymentIntentResults },
        });
      }
      if (paymentIntentResults?.paymentIntent?.status === 'succeeded') {
        // payment intent success
        polling = false;
        newtab?.close();
        setOverlay(false);
        window.parent.focus();
        setCheckoutStatus(ECheckoutStatus.PENDING);
        startOrderPolling(response.orderId);
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
          ...getEventParams(),
        });
      } else if (paymentIntentResults?.paymentIntent?.status === 'requires_payment_method') {
        // payment intent failed
        polling = false;
        newtab?.close();
        setOverlay(false);
        window.parent.focus();
        const errMessage = paymentIntentResults.paymentIntent.last_payment_error?.message;
        handleError(errMessage);
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
          ...getEventParams(),
          reason: errMessage,
        });
      } else if (paymentIntentResults?.error) {
        if (paymentIntentResults.error.code === 'lock_timeout' && retries > 0) {
          retries--;
        } else {
          // payment intent error
          polling = false;
          newtab?.close();
          setOverlay(false);
          window.parent.focus();
          const errMessage = paymentIntentResults.error.message;
          handleError(errMessage);
          sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
            ...getEventParams(),
            reason: errMessage,
          });
        }
      } else if (newtab?.closed) {
        setOverlay(false);
        window.parent.focus();
        polling = false;
        handleError(t('yourPaymentWasDeclined'));
      }
    }
  }

  async function handlePaymentNextAction(
    clientSecret: string,
    orderId: string,
    FEeventParams?: Partial<EventParams>
  ) {
    const paymentIntentResult = await stripe?.handleNextAction({
      clientSecret,
    });

    if (paymentIntentResult?.paymentIntent?.status === 'processing') {
      API.log({
        component: 'useSubmitPayment => handlePaymentNextAction',
        params: { paymentIntentResult },
      });
    }

    if (paymentIntentResult?.error) {
      const errorMessage = paymentIntentResult.error.message || '';
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        ...FEeventParams,
        reason: errorMessage,
      });
      handleError(errorMessage);
    } else if (
      paymentIntentResult?.paymentIntent?.status === 'requires_payment_method' ||
      paymentIntentResult?.paymentIntent?.status === 'requires_action'
    ) {
      const errorMessage = paymentIntentResult?.paymentIntent?.last_payment_error?.message || '';
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        ...FEeventParams,
        reason: errorMessage,
      });
      handleError(errorMessage);
    } else {
      // actions handled successfully
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
        ...getEventParams(),
        ...FEeventParams,
      });
      startOrderPolling(orderId, FEeventParams);
    }
  }

  function getPaymentMethodParams(
    paymentMethodName: string,
    zipCode?: string,
    fullAddress?: string
  ) {
    const cardParams: PaymentMethodCreateParams = {
      billing_details: {
        address: {
          postal_code: zipCode,
          line1: fullAddress,
          country: checkoutData?.customer.geoLocator.countryCode2,
        },
      },
    };
    const apmParams: PaymentMethodCreateParams = {
      billing_details: {
        address: {
          country: checkoutData?.customer.geoLocator.countryCode2,
        },
      },
    };
    const affirmAndAfterpayParams: PaymentMethodCreateParams = {};
    switch (paymentMethodName) {
      case EBEPaymentMethodsName.card:
        return cardParams;
      case EBEPaymentMethodsName.afterpay:
      case EBEPaymentMethodsName.affirm:
        return affirmAndAfterpayParams;
      default:
        return apmParams;
    }
  }

  const onExpressElementConfirm = (event: StripeExpressCheckoutElementConfirmEvent) => {
    const displayName = expressCheckoutEventToDisplayNameMapWithRouter[event.expressPaymentType];
    selectedPaymentMethodDisplayNameRef.current = displayName;
    const paymentMethod = checkoutData?.paymentMethods.find(
      (pm) => pm.paymentMethodDisplayName === displayName && pm.provider === EPaymentProvider.stripe
    );
    handleSubmit(true, paymentMethod?.id);
  };

  return {
    handleStripeSubmit: handleSubmit,
    onExpressElementConfirm,
  };
}
